import React, { useState, useEffect } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { API_URL } from '../../api/config';
import { PlusCircle, X, Edit2, Trash2, Users, Save, Plus, Coffee, Clock, CheckCircle, User, Grid3x3, Folder } from 'lucide-react';

const CustomTableManager = () => { 
  const { userToken: token, restaurantId } = useAuth();
  
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const [category, setCategory] = useState('Dining');
  const [tableName, setTableName] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [bulkCategory, setBulkCategory] = useState('Dining');
  const [bulkNamePrefix, setBulkNamePrefix] = useState('Table');
  const [bulkStartNumber, setBulkStartNumber] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  // Fetch tables
  const fetchTables = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/tables`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tables');
      const result = await response.json();
      setTables(result.data || []);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(result.data.map(t => t.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
      alert('Error: Failed to fetch tables');
    }
  };

  // Initialize default tables
  const initializeDefaultTables = async () => {
    if (!token) {
      alert('Authentication required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tables/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }
      
      await fetchTables();
      alert('Default tables created: 5 Dining + 2 Takeaway tables!');
    } catch (error) {
      console.error('Failed to initialize:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create single table
  const createTable = async () => {
    if (!category.trim() || !tableName.trim()) {
      alert('Category and table name are required');
      return;
    }

    if (!token) {
      alert('Authentication required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tables`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: category.trim(),
          tableName: tableName.trim(),
          tableNumber: tableNumber ? parseInt(tableNumber) : null,
          restaurantId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      await fetchTables();
      setShowAddModal(false);
      setCategory('Dining');
      setTableName('');
      setTableNumber('');
      setNewCategoryName('');
      alert('Table created successfully!');
    } catch (error) {
      console.error('Failed to create table:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create bulk tables
  const createBulkTables = async () => {
    if (!bulkCategory.trim() || !bulkNamePrefix.trim() || !bulkStartNumber || !bulkQuantity) {
      alert('All fields are required for bulk creation');
      return;
    }

    if (!token) {
      alert('Authentication required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tables/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: bulkCategory.trim(),
          namePrefix: bulkNamePrefix.trim(),
          baseNumber: parseInt(bulkStartNumber),
          quantity: parseInt(bulkQuantity),
          restaurantId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      await fetchTables();
      setShowBulkModal(false);
      setBulkCategory('Dining');
      setBulkNamePrefix('Table');
      setBulkStartNumber('');
      setBulkQuantity('');
      alert('Tables created successfully!');
    } catch (error) {
      console.error('Failed to create tables:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update table
  const updateTable = async () => {
    if (!editingTable || !category.trim() || !tableName.trim()) {
      alert('Category and table name are required');
      return;
    }

    if (!token) {
      alert('Authentication required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tables/${editingTable.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: category.trim(),
          tableName: tableName.trim(),
          tableNumber: tableNumber ? parseInt(tableNumber) : null
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      await fetchTables();
      setEditingTable(null);
      setCategory('Dining');
      setTableName('');
      setTableNumber('');
      alert('Table updated successfully!');
    } catch (error) {
      console.error('Failed to update table:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete table
  const deleteTable = async (tableId) => {
    if (!window.confirm('Are you sure you want to delete this table?')) return;

    if (!token) {
      alert('Authentication required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/tables/${tableId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete table');
      await fetchTables();
      alert('Table deleted successfully!');
    } catch (error) {
      console.error('Failed to delete table:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Toggle table status
  const toggleTableStatus = async (tableId, currentStatus) => {
    if (!token) {
      alert('Authentication required');
      return;
    }

    const newStatus = currentStatus === 'Available' ? 'Occupied' : 'Available';
    
    try {
      const response = await fetch(`${API_URL}/tables/${tableId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');
      await fetchTables();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Start editing
  const startEdit = (table) => {
    setEditingTable(table);
    setCategory(table.category);
    setTableName(table.tableName);
    setTableNumber(table.tableNumber?.toString() || '');
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingTable(null);
    setCategory('Dining');
    setTableName('');
    setTableNumber('');
  };

  useEffect(() => {
    if (token) {
      fetchTables();
    }
  }, [token]);

  // Filter tables by category
  const filteredTables = selectedCategory === 'All' 
    ? tables 
    : tables.filter(t => t.category === selectedCategory);

  // Stats calculation
  const availableTables = filteredTables.filter(t => t.status === 'Available').length;
  const occupiedTables = filteredTables.filter(t => t.status === 'Occupied').length;

  // Show loading or auth message if no token
  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="bg-white p-12 rounded-3xl shadow-xl border border-slate-100 text-center max-w-md">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Authentication Required</h3>
          <p className="text-gray-600">Please log in to manage your restaurant tables</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Table Management</h1>
            <p className="text-sm font-bold text-slate-400 mt-1">Organize tables by category</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {tables.length === 0 && (
              <button
                onClick={initializeDefaultTables}
                disabled={loading}
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 font-semibold transition-all flex items-center shadow-lg"
              >
                <Grid3x3 size={18} className="mr-2" />
                {loading ? 'Initializing...' : 'Initialize Tables'}
              </button>
            )}
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium transition-all flex items-center"
            >
              <PlusCircle size={18} className="mr-2" />
              Bulk Add
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-semibold transition-all flex items-center shadow-lg"
            >
              <Plus size={18} className="mr-2" />
              Add Table
            </button>
          </div>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${
                selectedCategory === 'All'
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              All Tables
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Folder size={16} className="mr-2" />
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Stats */}
        {filteredTables.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Tables</p>
                <Users size={20} className="text-slate-300" />
              </div>
              <h3 className="text-3xl font-black text-slate-900">{filteredTables.length}</h3>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Available</p>
                <CheckCircle size={20} className="text-emerald-400" />
              </div>
              <h3 className="text-3xl font-black text-emerald-600">{availableTables}</h3>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Occupied</p>
                <Clock size={20} className="text-blue-400" />
              </div>
              <h3 className="text-3xl font-black text-blue-600">{occupiedTables}</h3>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Categories</p>
                <Folder size={20} className="text-indigo-400" />
              </div>
              <h3 className="text-3xl font-black text-indigo-600">{categories.length}</h3>
            </div>
          </div>
        )}

        {/* Tables Grid */}
        {filteredTables.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users size={36} className="text-slate-400" />
            </div>
            <h3 className="text-2xl font-black text-slate-800 mb-2">No tables yet</h3>
            <p className="text-slate-400 text-sm font-bold mb-6">
              {tables.length === 0 
                ? 'Initialize default tables (5 Dining + 2 Takeaway) or create your first table'
                : 'No tables in this category'}
            </p>
            {tables.length === 0 && (
              <div className="flex gap-3 justify-center flex-wrap">
                <button
                  onClick={initializeDefaultTables}
                  disabled={loading}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 font-bold transition-all inline-flex items-center shadow-lg"
                >
                  <Grid3x3 size={18} className="mr-2" />
                  {loading ? 'Initializing...' : 'Initialize Default Tables'}
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-bold transition-all inline-flex items-center shadow-lg"
                >
                  <Plus size={18} className="mr-2" />
                  Create Custom Table
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filteredTables.map((table) => {
              const isOccupied = table.status === 'Occupied';

              return (
                <div
                  key={table.id}
                  className={`p-6 rounded-2xl shadow-lg flex flex-col h-full transition-all duration-300 ${
                    isOccupied
                      ? 'bg-[#0047FF] text-white hover:scale-[1.02]'
                      : 'bg-white border border-slate-100 hover:shadow-xl'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        isOccupied ? 'text-white/60' : 'text-slate-400'
                      }`}>
                        {table.category}
                      </span>
                      <h4 className={`text-lg font-black mt-1 ${
                        isOccupied ? 'text-white' : 'text-slate-800'
                      }`}>
                        {table.tableName}
                      </h4>
                    </div>
                    {table.tableNumber && (
                      <div className={`flex items-center gap-1.5 ${
                        isOccupied ? 'text-white/80' : 'text-slate-300'
                      }`}>
                        <User size={14} />
                        <span className="text-xs font-bold">{table.tableNumber}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 mb-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        isOccupied ? 'bg-white' : 'bg-emerald-500'
                      }`}></div>
                      <span className={`text-sm font-bold ${
                        isOccupied ? 'text-white' : 'text-slate-600'
                      }`}>
                        {table.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => toggleTableStatus(table.id, table.status)}
                      className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                        isOccupied
                          ? 'bg-white/20 hover:bg-white/30 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {isOccupied ? 'Free Up' : 'Assign'}
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(table)}
                        className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${
                          isOccupied
                            ? 'bg-white/20 hover:bg-white/30 text-white'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        <Edit2 size={14} className="inline mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => deleteTable(table.id)}
                        className={`py-2 px-3 rounded-xl text-xs transition-all ${
                          isOccupied
                            ? 'bg-white/20 hover:bg-white/30 text-white'
                            : 'bg-red-100 hover:bg-red-200 text-red-700'
                        }`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Table Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-900 px-6 py-4 text-white flex justify-between items-center rounded-t-2xl">
              <h3 className="text-xl font-black">Add New Table</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Category *</label>
                <select
                  value={category === '__new__' ? '__new__' : category}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setCategory('__new__');
                      setNewCategoryName('');
                    } else {
                      setCategory(e.target.value);
                      setNewCategoryName('');
                    }
                  }}
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {categories.length === 0 ? (
                    <>
                      <option value="Dining">Dining</option>
                      <option value="Takeaway">Takeaway</option>
                    </>
                  ) : (
                    categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  )}
                  <option value="__new__">+ New Category</option>
                </select>
              </div>

              {category === '__new__' && (
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">New Category Name *</label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => {
                      setNewCategoryName(e.target.value);
                      if (e.target.value.trim()) {
                        setCategory(e.target.value.trim());
                      }
                    }}
                    placeholder="e.g., Main Hall, VIP Section"
                    className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Table Name *</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  placeholder="e.g., Table A, Corner Booth"
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Table Number</label>
                <input
                  type="number"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  placeholder="e.g., 1, 2, 3..."
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={createTable}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 font-bold transition-all"
                >
                  {loading ? 'Creating...' : 'Create Table'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBulkModal(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-900 px-6 py-4 text-white flex justify-between items-center rounded-t-2xl">
              <h3 className="text-xl font-black">Bulk Add Tables</h3>
              <button onClick={() => setShowBulkModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Category *</label>
                <select
                  value={bulkCategory}
                  onChange={(e) => setBulkCategory(e.target.value)}
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {categories.length === 0 ? (
                    <>
                      <option value="Dining">Dining</option>
                      <option value="Takeaway">Takeaway</option>
                    </>
                  ) : (
                    categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Name Prefix *</label>
                <input
                  type="text"
                  value={bulkNamePrefix}
                  onChange={(e) => setBulkNamePrefix(e.target.value)}
                  placeholder="e.g., Table, VIP, Booth"
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Start Number *</label>
                  <input
                    type="number"
                    value={bulkStartNumber}
                    onChange={(e) => setBulkStartNumber(e.target.value)}
                    placeholder="1"
                    className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Quantity *</label>
                  <input
                    type="number"
                    value={bulkQuantity}
                    onChange={(e) => setBulkQuantity(e.target.value)}
                    placeholder="10"
                    min="1"
                    max="50"
                    className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>
              
              {bulkNamePrefix && bulkStartNumber && bulkQuantity && (
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                  <p className="text-xs text-blue-800 font-bold">
                    Creating {bulkQuantity} tables in <span className="font-black">{bulkCategory}</span>: "{bulkNamePrefix} {bulkStartNumber}" to "{bulkNamePrefix} {parseInt(bulkStartNumber) + parseInt(bulkQuantity) - 1}"
                  </p>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={createBulkTables}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 font-bold transition-all"
                >
                  {loading ? 'Creating...' : 'Create Tables'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {editingTable && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={cancelEdit}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-900 px-6 py-4 text-white flex justify-between items-center rounded-t-2xl">
              <h3 className="text-xl font-black">Edit Table</h3>
              <button onClick={cancelEdit} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Category *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Table Name *</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Table Number</label>
                <input
                  type="number"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl text-sm font-bold placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={cancelEdit}
                  className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={updateTable}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 font-bold transition-all flex items-center justify-center"
                >
                  <Save size={16} className="mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTableManager;